import { Button, Grid, useTheme, Theme, Box } from "@mui/material";
import { IDisplayPairing, IFullFlight, IGroundEvent, INonFlyingAssignment } from "@vmosolutions/oc-interfaces";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { EventChip } from "src/components/Flights/EventChip";
import { FlightChip } from "src/components/Flights/FlightChip";
import { SelectField } from "src/components/FormikComponents/SelectField";
import { ShortCodeChip } from "src/components/ShortCodes/ShortCodeChip";
import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";
import { getEventHistory } from "src/redux/actions/History/History.Actions";
import { HistoryTable } from "./HistoryTable";

interface IHistoryWindowProps {
  id: string;
  path: string[];
  windowProps: {
    collection: string;
    id: string;
    flight?: IFullFlight;
    pairing?: IDisplayPairing;
    groundEvent?: IGroundEvent;
    assignment?: INonFlyingAssignment;
  };
}
interface IChangedField {
  prev?: string;
  new: string;
}
export interface IDocChange {
  action: string;
  timeOfChange: string;
  fieldsAdded: [{ key: string; value: string }];
  fieldsRemoved: [{ key: string; value: string }];
  fieldsChanged: [{ key: string; value: IChangedField }];
  modBy: string;
  modTime: number;
  docId: string;
}

const HistoryWindowStyles = (theme: Theme): any => ({
  width: "100%",
  " .text": {
    margin: "1rem",
  },
  "& .item_chip": {
    padding: 5,
  },
  "& .history_table_item": {
    minWidth: 650,
    overflowX: "auto",
  },
  "& .history_cell": {
    "& .history_cell_accordion": {
      backgroundColor: "inherit",
      "& .history_cell_accordion_summary": {
        minHeight: "unset",
      },
    },
  },
  "& .field_update_type": {
    textAlign: "center",
    padding: "4px 4px",
    "& .update_type_font": {
      fontSize: "14px",
    },
  },
  "& .history_details": {
    paddingTop: 2,
    paddingBottom: 2,
    display: "grid",
    "& .history_item": {
      display: "flex",
      flexGrow: 1,
      padding: "1px 1px",
      "& .field": {
        width: "33%",
        display: "flex",
        overflowWrap: "anywhere",
      },
      "& .modified": {
        color: theme.custom.history.modfiedField,
        width: "33%",
        display: "flex",
        overflowWrap: "anywhere",
        padding: "0px 10px",
      },
      "& .added": {
        color: theme.custom.history.addedField,
        width: "33%",
        display: "flex",
        overflowWrap: "anywhere",
      },
    },
  },
  "& .refresh_btn": {
    margin: 5,
    padding: "2px 8px",
    height: 20,
    fontSize: 10,
    "& .refresh_icon": {
      width: "0.8em",
      height: "0.8em",
    },
  },
});

const collectionTypeOptions = [
  { value: "Flight", label: "Deleted Flights", collection: "flights", action: "delete" },
  { value: "Pairing", label: "Deleted Pairings", collection: "pairings", action: "delete" },
  { value: "GroundEvent", label: "Deleted Ground Events", collection: "groundevents", action: "delete" },
];

export const HistoryWindow = (props: IHistoryWindowProps): JSX.Element => {
  const { windowProps, path } = props;
  const { id, collection, flight, pairing, groundEvent } = windowProps;
  const theme = useTheme();

  const containerRef = useRef<HTMLDivElement>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [collectionType, setCollectionType] = useState<any | null>(null);
  const [history, setHistory] = useState<any | null>(null);
  const [tableMaxHeight, setTableMaxHeight] = useState<number>(0);

  useEffect(() => {
    const getEventHistoryAction = async (): Promise<void> => {
      setIsLoading(true);
      const historyResponse: any[] = await getEventHistory(collection, id);
      if (Array.isArray(historyResponse))
        setHistory(historyResponse.sort((a: any, b: any) => (a.timeOfChange > b.timeOfChange ? -1 : 1)));
      setIsLoading(false);
    };
    if (id && collection) {
      getEventHistoryAction();
    }
  }, [id, collection]);

  const getDeletedDocumentsAction = useCallback(async (collection: string, action: string): Promise<void> => {
    setIsLoading(true);
    let fetching = true;
    let id = "";
    const allHistory: IDocChange[] = [];
    while (fetching) {
      const collectionResponse = await AmplifyAPIWrapper.Get<{
        results: IDocChange[];
        lastId: string;
      }>(`/history/collection/${collection}/${action}${id ? `/${id}` : ""}`);
      if (Array.isArray(collectionResponse?.results)) {
        allHistory.push(...collectionResponse.results);
      }
      if (collectionResponse?.lastId) id = collectionResponse?.lastId;
      else {
        fetching = false;
      }
    }

    if (Array.isArray(allHistory)) {
      setHistory(allHistory.sort((a: any, b: any) => (a.timeOfChange > b.timeOfChange ? -1 : 1)));
    } else {
      setHistory(null);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (collectionType)
      void getDeletedDocumentsAction(collectionType.collection as string, collectionType.action as string);
  }, [collectionType, getDeletedDocumentsAction]);

  useLayoutEffect(() => {
    setTimeout(() => {
      const { height } = containerRef?.current?.getBoundingClientRect() ?? {};
      const headerSize = 250;
      setTableMaxHeight(height - headerSize);
    }, 100);
  }, [path]);

  if (id && collection) {
    return (
      <Box sx={HistoryWindowStyles(theme)}>
        <SpinnerProgress className="loading_spinner" loading={isLoading} message="loading..." />
        <div className="item_chip">
          {groundEvent && <EventChip data={groundEvent} />}
          {flight && <FlightChip data={flight} />}
          {pairing && <ShortCodeChip code={pairing.shortId} itemId={pairing._id} type="pairing" edit={true} />}
        </div>
        <Grid container direction="column">
          <HistoryTable
            id={props.id}
            path={path}
            tableMaxHeight={tableMaxHeight}
            history={history}
            type={collectionType}
          />
        </Grid>
      </Box>
    );
  } else {
    return (
      <Box sx={HistoryWindowStyles(theme)}>
        <SpinnerProgress className="loading_spinner" loading={isLoading} message="loading..." />
        <Grid container style={{ width: "auto" }} alignItems="center">
          <Grid item style={{ width: 263 }}>
            <SelectField
              name="eventType"
              label="Event Type"
              isMulti={false}
              onChange={(value: any) => setCollectionType(value)}
              value={collectionType}
              options={collectionTypeOptions ?? []}
              menuPosition="fixed"
              isSearchable={true}
              isClearable={false}
            />
          </Grid>

          <Button
            className="refresh_btn"
            id={"refresh_historyref resh_btn"}
            onClick={() => getDeletedDocumentsAction(collectionType.collection, collectionType.action)}
            color="primary"
            disabled={isLoading || !collectionType}
            variant="contained"
          >
            Refresh
          </Button>
        </Grid>
        <Grid container sx={HistoryWindowStyles(theme)} direction="column">
          <HistoryTable
            id={props.id}
            path={path}
            tableMaxHeight={tableMaxHeight}
            history={history}
            type={collectionType}
          />
        </Grid>
      </Box>
    );
  }
};
